import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Group, SegmentedControl, Stack } from '@mantine/core'
import { AppSkeleton, AppButton, AppText, EmptyState } from '../../app/common/ui'
import agent from '../../app/api/agent'
import type { PollListItemDto } from '../../app/models/poll'
import { useStore } from '../../app/stores/store'
import { canCreatePoll } from '../../app/common/constants/reputation'

function formatTimeLeft (expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (ms <= 0) return 'Ended'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`
}

export default observer(function PollsPage () {
  const { userStore } = useStore()
  const [filter, setFilter] = useState<'active' | 'ended'>('active')
  const [items, setItems] = useState<PollListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [disabled, setDisabled] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 20

  useEffect(() => {
    setPageNumber(1)
    setLoading(true)
    setDisabled(false)
    agent.Polls.list({ active: filter === 'active', pageNumber: 1, pageSize })
      .then((r) => {
        setItems(r.items)
        setTotalCount(r.totalCount)
      })
      .catch((err: { response?: { status?: number } }) => {
        if (err.response?.status === 403) setDisabled(true)
        else setItems([])
      })
      .finally(() => setLoading(false))
  }, [filter])

  const loadMore = () => {
    const next = pageNumber + 1
    setPageNumber(next)
    agent.Polls.list({ active: filter === 'active', pageNumber: next, pageSize })
      .then((r) => {
        setItems((prev) => [...prev, ...r.items])
        setTotalCount(r.totalCount)
      })
      .catch(() => {})
  }

  if (disabled) {
    return (
      <Box sx={{ padding: '16px 24px', maxWidth: 720, margin: '0 auto' }}>
        <EmptyState title="Polls are off" message="Enable polls-enabled in Overseer after the schema migration." />
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '16px 24px', maxWidth: 720, margin: '0 auto' }}>
      <Group position="apart" mb="md">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20 }}>POLLS</AppText>
        {userStore.isLoggedIn && canCreatePoll(userStore.user) && (
          <AppButton component={Link} to="/polls/new" color="ember" size="sm">
            + New poll
          </AppButton>
        )}
      </Group>

      <AppText size="xs" color="dimmed" mb="sm">
        Sitewide polls — quick votes for the whole community.
      </AppText>

      <SegmentedControl
        mb="md"
        value={filter}
        onChange={(v) => setFilter(v as 'active' | 'ended')}
        data={[
          { label: 'Active', value: 'active' },
          { label: 'Ended', value: 'ended' }
        ]}
      />

      {loading
        ? (
        <Stack spacing="sm">
          {[1, 2, 3].map((i) => <AppSkeleton key={i} height={72} radius="md" />)}
        </Stack>
          )
        : items.length === 0
          ? (
        <EmptyState
          title={filter === 'active' ? 'No active polls' : 'No ended polls yet'}
          message={filter === 'active' ? 'Be the first to start one.' : undefined}
        />
            )
          : (
        <Stack spacing="sm">
          {items.map((p) => (
            <Box
              key={p.id}
              component={Link}
              to={`/polls/${p.id}`}
              sx={{
                display: 'block',
                padding: 16,
                borderRadius: 8,
                border: '1px solid var(--gs-border)',
                background: 'var(--gs-surface)',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { borderColor: 'var(--gs-accent)' }
              }}
            >
              <AppText weight={600} size="sm" mb={4}>
                {p.question}
              </AppText>
              <Group spacing="xs">
                <AppText size="xs" color="dimmed">
                  @{p.authorUsername ?? 'unknown'} · {formatTimeLeft(p.expiresAt)}
                </AppText>
                {p.totalVotes != null && (
                  <AppText size="xs" color="dimmed">
                    · {p.totalVotes} vote{p.totalVotes === 1 ? '' : 's'}
                  </AppText>
                )}
                {p.optionCount != null && p.optionCount > 0 && (
                  <AppText size="xs" color="dimmed">
                    · {p.optionCount} option{p.optionCount === 1 ? '' : 's'}
                  </AppText>
                )}
              </Group>
            </Box>
          ))}
          {items.length < totalCount && (
            <AppButton variant="light" onClick={loadMore}>
              Load more
            </AppButton>
          )}
        </Stack>
            )}
    </Box>
  )
})
